import { useFormContext, useWatch } from 'react-hook-form';

import { getBopisFulfillmentStatus } from '@libs/shared-ui';

import { isDeliveryMethodAvailable, useProduct } from '@/entities/product';
import { type FormFields, usePurchaseFormWatch } from '@/entities/purchase-form';

export const useBopisDeliveryTile = () => {
    const { product, tailored: productTailored, isLoading, isError } = useProduct();
    const tailored = isError ? undefined : productTailored;

    const storeId = product?.sku && tailored?.variants?.[product.sku].BOPIS?.storeId;

    const { control, register } = useFormContext<FormFields>();

    const selectedPurchaseOption = usePurchaseFormWatch('purchaseOption');
    const autoshipFrequency = usePurchaseFormWatch('autoshipFrequency');
    const selectedDeliveryMethod = useWatch({ control, name: 'fulfilmentMethod' });

    const isBopisAvailableForStore =
        !isLoading && storeId ? tailored?.bopisStoreData?.[storeId]?.offersBopis : undefined;

    // Check if BOPIS is available for the current purchase option
    const isBopisAvailableForPurchaseOption = isDeliveryMethodAvailable({
        deliveryMethodName: 'BOPIS',
        deliveryMethodData: product?.deliveryMethods?.BOPIS,
        purchaseOption: selectedPurchaseOption ?? 'one-time',
    });

    // BOPIS availability is determined by a hierarchy of checks:
    // 1. Check if BOPIS is enabled for the selected purchase option
    //    (if disabled here, BOPIS is unavailable regardless of other factors)
    // 2. Check if the selected store offers BOPIS
    //    (defaults to true if no store data exists)
    // 3. Check if the selected store has stock inventory for the item
    //    (only evaluated if both checks above pass)
    // 4. Disable BOPIS when "One Time" frequency is selected for autoship
    const isBopisAvailable = isBopisAvailableForPurchaseOption ? (isBopisAvailableForStore ?? true) : false;
    const isOneTimeFrequencySelected = selectedPurchaseOption === 'autoship' && autoshipFrequency === 'one_time';

    const isDisabled = !isLoading && (!isBopisAvailable || isOneTimeFrequencySelected);

    const availableStock = product?.sku ? (tailored?.variants?.[product.sku]?.BOPIS?.availableStock ?? 0) : 0;
    const altAvailableStock = product?.sku ? (tailored?.variants?.[product.sku]?.BOPIS?.altAvailableStock ?? 0) : 0;

    const status = getBopisFulfillmentStatus({
        availableStock,
        minimumCaseQty: product?.deliveryMethods?.BOPIS?.purchaseQuantities?.min ?? 1,
        altAvailableStock,
        isStoreSelected: !!storeId,
        isPharmacy: !!product?.pharmacy,
    });

    return {
        isLoading,
        isDisabled,
        status,
        selectedDeliveryMethod,
        register: register('fulfilmentMethod'),
    };
};
